'use client';

import { MarketType } from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	calculateMedian,
	getTieredSortScore,
} from '@velocity-exchange/common';
import { useMemo } from 'react';
import use24hMarketDataFromStore from './use24hMarketDataFromStore';
import useFavouriteMarkets from './useFavouriteMarkets';
import { marketIsHot } from '../utils/market';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import { useSpotMarkets } from './spotMarkets/useSpotMarkets';
import { usePerpMarkets } from './perpMarkets/usePerpMarkets';
import { NEW_MARKET_CUT_OFF_TIME } from '../constants/constants';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

export type MarketSortingInfo = {
	marketIndex: number;
	marketType: MarketType;
	volume: number;
	marketCap: number | undefined;
	isNew: boolean;
	isFavourited: boolean;
	isHot: boolean;
	baseAssetSymbol: string;
	favouriteIndex: number;
	isSol?: boolean;
	isVelocity?: boolean;
};

const getSortingScoresForMarket = (
	sortingInfo: MarketSortingInfo,
	sortBy: SortCriteria[] = []
) => {
	const volumeScore = sortBy.includes('24hVolume')
		? (sortingInfo.volume ?? 0)
		: 0;

	return [
		sortBy.includes('isFavorited') && sortingInfo.isFavourited
			? 2 + (volumeScore || sortingInfo.favouriteIndex)
			: 0,
		sortBy.includes('isSol') && sortingInfo.isSol ? 1 : 0,
		sortBy.includes('isDrift') && sortingInfo.isVelocity ? 1 : 0,
		sortBy.includes('isNew') && sortingInfo.isNew ? 1 + volumeScore : 0,
		sortBy.includes('isHot') && sortingInfo.isHot ? 1 + volumeScore : 0,
		// I don't think it really makes sense to sort by both `24hVolume` and `marketCap`,
		// or `24hVolume` and `marketIndex`, so just let `24hVolume` override them.
		sortBy.includes('24hVolume') ? volumeScore : 0,
		sortBy.includes('marketCap') ? (sortingInfo.marketCap ?? 0) : 0,
		// Lower market indexes are "higher" in sorting
		sortBy.includes('marketIndex') ? sortingInfo.marketIndex * -1 : 0,
	];
};

type SortCriteria =
	| 'isFavorited'
	| '24hVolume'
	| 'isSol'
	| 'isDrift'
	| 'isNew'
	| 'isHot'
	| 'marketCap'
	| 'marketIndex';

interface Params {
	marketType: MarketType | undefined;
	sortBy?: SortCriteria[];
}

/**
 * Returns markets sorted by specified criteria.
 *
 * If `sortBy` includes `24hVolume`, then `isFavorited`, `isNew`, and `isHot` will be
 * sorted in conjunction with `24hVolume`. That is, favorite, new, and hot markets
 * will appear first (sorted by volume), then the rest of the markets will follow
 * (also sorted by volume). `24hVolume` overrides `marketCap` and `marketIndex`.
 *
 * Precedence: `isFavorited`, `isSol`, `isVelocity`, `isNew`, `isHot`, `marketCap`, `marketIndex`.
 */
const useMarketsSortedByCriteria = ({
	marketType,
	sortBy,
}: Params): MarketSortingInfo[] => {
	const marketsData24H = use24hMarketDataFromStore();
	const { getFavouriteMarketIndex, favoriteMarkets } = useFavouriteMarkets();
	const velocityClient = useVelocityClient();
	const isVelocityClientReady = useVelocityClientIsReady();

	const spotMarkets = useSpotMarkets();
	const perpMarkets = usePerpMarkets();

	/**
	 * Calculate whether markets are hot by comparing their daily volume z scores to the average z score for the market
	 */
	const hotMarketData: { [marketKey: string]: boolean } = useMemo(() => {
		if (marketsData24H.length === 0) return {};

		const idAndZScoreAndRawVol = marketsData24H.map((m) => {
			return [
				new MarketId(m.marketIndex, m.marketType),
				m.dailyVolumeIncreaseZScore,
				m.quoteVolume,
			] as [MarketId, number, number];
		});

		const medianZScore = calculateMedian(idAndZScoreAndRawVol.map((a) => a[1]));

		return idAndZScoreAndRawVol.reduce(
			(previous, [currentMarketId, currentMarketZScore, currentVol]) => {
				return {
					...previous,
					[currentMarketId.key]: marketIsHot(
						currentMarketZScore,
						medianZScore,
						currentVol
					),
				};
			},
			{} as { [marketKey: string]: boolean }
		);
	}, [marketsData24H]);

	const marketsFilteredByMarketType = useMemo((): {
		marketIndex: number;
		marketType: MarketType;
		baseAssetSymbol: string;
		launchTs: number;
	}[] => {
		if (!marketType) {
			return [];
		}

		if (ENUM_UTILS.match(marketType, MarketType.PERP)) {
			return perpMarkets.map((mkt) => ({
				marketIndex: mkt.marketIndex,
				marketType: MarketType.PERP,
				baseAssetSymbol: mkt.baseAssetSymbol,
				launchTs: mkt.launchTs,
			}));
		}

		return spotMarkets
			.map((mkt) => ({
				marketIndex: mkt.marketIndex,
				marketType: MarketType.SPOT,
				baseAssetSymbol: mkt.symbol,
				launchTs: mkt?.launchTs ?? 0, // No launchTs on spot markets for some reason
			}))
			.filter((mkt) => mkt.marketIndex !== 0); // Filter out USDC
	}, [marketType, spotMarkets, perpMarkets]);

	// `sortBy` comes from the consumer and may not be memo'd, so use a synced ref.
	const sortByRef = useSyncedRef(sortBy);
	const sortedMarkets = useMemo(() => {
		if (!velocityClient || !isVelocityClientReady) {
			return [];
		}

		const mappedMarkets = marketsFilteredByMarketType.map((mktInfo) => {
			const marketId = new MarketId(mktInfo.marketIndex, mktInfo.marketType);

			const marketData = marketsData24H.find(
				(mktData24h) =>
					mktData24h.marketIndex === mktInfo.marketIndex &&
					ENUM_UTILS.match(mktInfo.marketType, mktData24h.marketType)
			);

			const favouriteIndex = getFavouriteMarketIndex(marketId);

			return {
				marketIndex: mktInfo.marketIndex,
				marketType: mktInfo.marketType,
				baseAssetSymbol: mktInfo.baseAssetSymbol,
				volume: marketData?.quoteVolume ?? 0,
				isNew: mktInfo.launchTs > NEW_MARKET_CUT_OFF_TIME,
				isFavourited: favouriteIndex !== -1,
				isHot: hotMarketData[marketId.key] ?? false,
				marketCap: marketData?.marketCap,
				favouriteIndex: favoriteMarkets.length - favouriteIndex,
				isSol: mktInfo.baseAssetSymbol === 'SOL',
				isVelocity: mktInfo.baseAssetSymbol === 'DRIFT',
			};
		});

		return [...mappedMarkets].sort((mktA, mktB) => {
			// Passing [B, A] into the method because we want to sort by descending rank.
			return getTieredSortScore(
				getSortingScoresForMarket(mktB as MarketSortingInfo, sortByRef.current),
				getSortingScoresForMarket(mktA as MarketSortingInfo, sortByRef.current)
			);
		});
	}, [
		velocityClient,
		isVelocityClientReady,
		marketsFilteredByMarketType,
		marketsData24H,
		getFavouriteMarketIndex,
		hotMarketData,
		favoriteMarkets.length,
		sortByRef,
	]);

	return sortedMarkets;
};

export default useMarketsSortedByCriteria;
